import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Download, 
  Upload, 
  Database, 
  AlertTriangle,
  CheckCircle,
  Loader2,
  HardDrive,
  Calendar,
  FileJson,
  Cloud,
  Settings,
  Clock,
  ExternalLink,
  Unplug,
  RefreshCw,
  ShieldCheck
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import api from '../services/api';

const COLLECTIONS = [
  { id: 'users', name: 'Usuarios', description: 'Usuarios y permisos del sistema', icon: '👥' },
  { id: 'categories', name: 'Categorías', description: 'Categorías de equipos', icon: '📁' },
  { id: 'locations', name: 'Ubicaciones', description: 'Ubicaciones físicas', icon: '📍' },
  { id: 'responsibles', name: 'Responsables', description: 'Personas que reciben equipo', icon: '👤' },
  { id: 'equipment', name: 'Equipos', description: 'Tipos de equipos', icon: '💻' },
  { id: 'equipment_locations', name: 'Stock por Ubicación', description: 'Inventario agregado', icon: '📦' },
  { id: 'equipment_units', name: 'Unidades', description: 'Unidades serializadas', icon: '🔢' },
  { id: 'movements', name: 'Movimientos', description: 'Salidas y retornos', icon: '🔄' },
  { id: 'movement_items', name: 'Items de Movimiento', description: 'Detalle de movimientos', icon: '📋' },
  { id: 'audit_log', name: 'Auditoría (legacy)', description: 'Registro de actividades', icon: '📝' },
  { id: 'audit', name: 'Auditoría', description: 'Logs de auditoría completos', icon: '📝' },
  { id: 'config', name: 'Configuración', description: 'Estilos y preferencias', icon: '⚙️' },
  { id: 'announcements', name: 'Anuncios', description: 'Mensajes del sistema', icon: '📢' },
  { id: 'maintenance', name: 'Mantenimiento', description: 'Registros de mantenimiento', icon: '🔧' },
  { id: 'reservations', name: 'Reservas', description: 'Reservas de equipo', icon: '📅' },
  { id: 'notifications', name: 'Notificaciones', description: 'Notificaciones del sistema', icon: '🔔' },
  { id: 'system_logs', name: 'Logs del Sistema', description: 'Logs del debugger', icon: '🖥️' },
  { id: 'role_permissions', name: 'Permisos de Rol', description: 'Configuración de permisos', icon: '🔑' },
  { id: 'changelog', name: 'Changelog', description: 'Historial de versiones', icon: '📜' },
  { id: 'checklists', name: 'Checklists', description: 'Listas de verificación', icon: '✅' },
  { id: 'condition_history', name: 'Historial de Condición', description: 'Cambios de estado de equipos', icon: '📊' },
  { id: 'events', name: 'Eventos', description: 'Eventos del calendario', icon: '🗓️' },
  { id: 'scheduled_reports', name: 'Reportes Programados', description: 'Config de reportes automáticos', icon: '📈' },
];

const BackupPage = () => {
  const [searchParams] = useSearchParams();
  const [backupInfo, setBackupInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [uploadingToOneDrive, setUploadingToOneDrive] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  
  // Selective backup
  const [selectedCollections, setSelectedCollections] = useState(COLLECTIONS.map(c => c.id));
  const [backupType, setBackupType] = useState('all');
  
  // Scheduler status
  const [schedulerStatus, setSchedulerStatus] = useState({
    scheduler_running: false,
    job_scheduled: false,
    next_run: null,
    recent_backups: []
  });
  
  // OneDrive status
  const [oneDriveStatus, setOneDriveStatus] = useState({
    connected: false,
    user_name: null,
    user_email: null,
    last_backup: null,
    auto_enabled: false,
    frequency: 'weekly'
  });
  const [loadingOneDrive, setLoadingOneDrive] = useState(true);
  
  // OneDrive backup list and restore
  const [oneDriveBackups, setOneDriveBackups] = useState([]);
  const [loadingOneDriveBackups, setLoadingOneDriveBackups] = useState(false);
  const [showOneDriveRestoreDialog, setShowOneDriveRestoreDialog] = useState(false);
  const [selectedOneDriveBackup, setSelectedOneDriveBackup] = useState(null);
  const [restoringFromOneDrive, setRestoringFromOneDrive] = useState(false);

  // Atlas backup (dual-write)
  const [atlasStatus, setAtlasStatus] = useState(null);
  const [loadingAtlas, setLoadingAtlas] = useState(true);
  const [syncingAtlas, setSyncingAtlas] = useState(false);
  const [verifyingAtlas, setVerifyingAtlas] = useState(false);
  const [atlasVerify, setAtlasVerify] = useState(null);
  const [showAtlasSyncDialog, setShowAtlasSyncDialog] = useState(false);
  const [repairingAtlas, setRepairingAtlas] = useState(false);
  const [restoringAtlas, setRestoringAtlas] = useState(false);
  const [showAtlasRestoreDialog, setShowAtlasRestoreDialog] = useState(false);

  useEffect(() => {
    loadBackupInfo();
    loadOneDriveStatus();
    loadSchedulerStatus();
    loadAtlasStatus();
    
    // Handle OAuth callback params
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const errorDetail = searchParams.get('error_detail');
    
    if (success === 'connected') {
      toast.success('¡OneDrive conectado exitosamente!');
      loadOneDriveStatus();
      loadSchedulerStatus();
      // Clean URL
      window.history.replaceState({}, '', '/backup');
    } else if (error) {
      // Show detailed error message
      const errorMessages = {
        'oauth_error': 'Error de autenticación con Microsoft',
        'no_code': 'No se recibió código de autorización',
        'no_state': 'Parámetro de seguridad faltante',
        'no_stored_state': 'Sesión expirada, intenta de nuevo',
        'invalid_state': 'Sesión inválida, intenta de nuevo',
        'config_error': 'Error de configuración del servidor',
        'token_error': 'Error al obtener token de acceso',
        'no_token': 'Microsoft no devolvió token',
        'timeout': 'Tiempo de espera agotado',
        'exception': 'Error inesperado'
      };
      
      const mainError = errorMessages[error] || error;
      const detail = errorDetail ? decodeURIComponent(errorDetail) : '';
      
      if (detail) {
        toast.error(`${mainError}: ${detail}`, { duration: 8000 });
      } else {
        toast.error(`Error al conectar OneDrive: ${mainError}`, { duration: 5000 });
      }
      
      console.error('OneDrive connection error:', { error, errorDetail: detail });
      window.history.replaceState({}, '', '/backup');
    }
  }, [searchParams]);

  const loadAtlasStatus = async () => {
    try {
      setLoadingAtlas(true);
      const response = await api.get('/admin/backup/atlas-status');
      setAtlasStatus(response.data);
    } catch (error) {
      setAtlasStatus({ enabled: false, reachable: false, message: 'No se pudo cargar el estado de Atlas' });
    } finally {
      setLoadingAtlas(false);
    }
  };

  const runAtlasSync = async () => {
    try {
      setSyncingAtlas(true);
      setShowAtlasSyncDialog(false);
      const response = await api.post('/admin/backup/atlas-sync');
      const total = Object.values(response.data.synced || {}).reduce((a, b) => a + b, 0);
      toast.success(`Copia inicial completada: ${total} documentos respaldados en Atlas`);
      loadAtlasStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ejecutar la copia inicial a Atlas');
    } finally {
      setSyncingAtlas(false);
    }
  };

  const runAtlasVerify = async () => {
    try {
      setVerifyingAtlas(true);
      const response = await api.get('/admin/backup/atlas-verify');
      setAtlasVerify(response.data);
      if (response.data.all_match) {
        toast.success('Verificación OK: todos los conteos coinciden con la base principal');
      } else {
        toast.warning('Hay diferencias de conteo entre la base principal y Atlas');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al verificar Atlas');
    } finally {
      setVerifyingAtlas(false);
    }
  };

  const repairAtlasDiffs = async () => {
    const mismatched = (atlasVerify?.collections || []).filter(c => !c.match).map(c => c.collection);
    if (!mismatched.length) return;
    try {
      setRepairingAtlas(true);
      const response = await api.post('/admin/backup/atlas-sync', { collections: mismatched });
      const total = Object.values(response.data.synced || {}).reduce((a, b) => a + b, 0);
      toast.success(`Reparadas ${mismatched.length} colección(es) (${total} documentos copiados a Atlas)`);
      runAtlasVerify();
      loadAtlasStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reparar diferencias');
    } finally {
      setRepairingAtlas(false);
    }
  };

  const runAtlasRestore = async () => {
    try {
      setRestoringAtlas(true);
      setShowAtlasRestoreDialog(false);
      const response = await api.post('/admin/backup/atlas-restore');
      const total = Object.values(response.data.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Restauración completada: ${total} documentos traídos desde Atlas`);
      loadBackupInfo();
      runAtlasVerify();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar desde Atlas');
    } finally {
      setRestoringAtlas(false);
    }
  };

  const loadBackupInfo = async () => {    try {
      setLoading(true);
      const response = await api.get('/backup/info');
      setBackupInfo(response.data);
    } catch (error) {
      console.error('Error loading backup info:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOneDriveStatus = async () => {
    try {
      setLoadingOneDrive(true);
      const response = await api.get('/backup/onedrive/status');
      setOneDriveStatus(response.data);
    } catch (error) {
      console.log('OneDrive not configured or connected');
      setOneDriveStatus({ connected: false });
    } finally {
      setLoadingOneDrive(false);
    }
  };

  const loadSchedulerStatus = async () => {
    try {
      const response = await api.get('/backup/scheduler-status');
      setSchedulerStatus(response.data);
    } catch (error) {
      console.log('Could not load scheduler status');
    }
  };

  const handleDownloadBackup = async () => {
    try {
      setDownloading(true);
      
      const collections = backupType === 'all' ? null : selectedCollections;
      
      const response = await api.get('/backup/download', {
        params: { collections: collections?.join(',') },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `backup_cmidiomas_${new Date().toISOString().slice(0,10)}.json`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup descargado correctamente');
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error('Error al descargar backup');
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast.error('Solo se permiten archivos JSON');
        return;
      }
      setSelectedFile(file);
      setShowRestoreDialog(true);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) return;
    
    try {
      setRestoring(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await api.post('/backup/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Base de datos restaurada correctamente');
      
      if (response.data.errors?.length > 0) {
        toast.warning(`Algunos errores: ${response.data.errors.join(', ')}`);
      }
      
      loadBackupInfo();
    } catch (error) {
      console.error('Error restoring backup:', error);
      toast.error(error.response?.data?.detail || 'Error al restaurar backup');
    } finally {
      setRestoring(false);
      setShowRestoreDialog(false);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const toggleCollection = (collectionId) => {
    setSelectedCollections(prev => 
      prev.includes(collectionId) 
        ? prev.filter(c => c !== collectionId)
        : [...prev, collectionId]
    );
  };

  const selectAllCollections = () => setSelectedCollections(COLLECTIONS.map(c => c.id));
  const deselectAllCollections = () => setSelectedCollections([]);

  const handleConnectOneDrive = async () => {
    try {
      const response = await api.get('/backup/onedrive/auth-url');
      // Redirect to Microsoft login
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Error getting OneDrive auth URL:', error);
      toast.error(error.response?.data?.detail || 'Error al conectar con OneDrive');
    }
  };

  const handleDisconnectOneDrive = async () => {
    try {
      await api.post('/backup/onedrive/disconnect');
      toast.success('OneDrive desconectado');
      setOneDriveStatus({ connected: false });
      setShowDisconnectDialog(false);
    } catch (error) {
      toast.error('Error al desconectar OneDrive');
    }
  };

  const handleUploadToOneDrive = async () => {
    try {
      setUploadingToOneDrive(true);
      const response = await api.post('/backup/onedrive/upload');
      toast.success(`Backup subido a OneDrive: ${response.data.filename}`);
      
      if (response.data.web_url) {
        toast.info(
          <div className="flex items-center gap-2">
            <span>Ver archivo en OneDrive</span>
            <a href={response.data.web_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        );
      }
      
      loadOneDriveStatus();
      loadOneDriveBackupList(); // Refresh backup list
    } catch (error) {
      console.error('Error uploading to OneDrive:', error);
      toast.error(error.response?.data?.detail || 'Error al subir backup a OneDrive');
    } finally {
      setUploadingToOneDrive(false);
    }
  };

  const loadOneDriveBackupList = async () => {
    if (!oneDriveStatus.connected) return;
    
    try {
      setLoadingOneDriveBackups(true);
      const response = await api.get('/backup/onedrive-list');
      setOneDriveBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error loading OneDrive backups:', error);
      setOneDriveBackups([]);
    } finally {
      setLoadingOneDriveBackups(false);
    }
  };

  const handleRestoreFromOneDrive = async () => {
    if (!selectedOneDriveBackup) return;
    
    try {
      setRestoringFromOneDrive(true);
      const response = await api.post('/backup/restore-from-onedrive', {
        file_id: selectedOneDriveBackup.id
      });
      
      toast.success(`Restauración completada desde: ${selectedOneDriveBackup.name}`);
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`Algunos errores: ${response.data.errors.join(', ')}`);
      }
      
      setShowOneDriveRestoreDialog(false);
      setSelectedOneDriveBackup(null);
      loadBackupInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar desde OneDrive');
    } finally {
      setRestoringFromOneDrive(false);
    }
  };

  // Load backup list when OneDrive is connected
  useEffect(() => {
    if (oneDriveStatus.connected) {
      loadOneDriveBackupList();
    }
  }, [oneDriveStatus.connected]);

  const handleSaveAutoConfig = async () => {
    try {
      const response = await api.post('/backup/auto-config', {
        enabled: oneDriveStatus.auto_enabled,
        frequency: oneDriveStatus.frequency,
        time_of_day: `${oneDriveStatus.preferred_hour || '03'}:00`,
        retention_days: parseInt(oneDriveStatus.retention) || 30,
        onedrive_connected: oneDriveStatus.connected,
        onedrive_folder: 'CMIdiomas_Backups'
      });
      toast.success('Configuración guardada');
      
      // Reload scheduler status to show next run time
      loadSchedulerStatus();
      
      if (response.data.next_scheduled_backup) {
        toast.info(`Próximo backup programado: ${new Date(response.data.next_scheduled_backup).toLocaleString()}`);
      }
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  const handleRunBackupNow = async () => {
    try {
      setUploadingToOneDrive(true);
      await api.post('/backup/run-now');
      toast.success('Backup iniciado. Revisa el historial en unos momentos.');
      
      // Reload scheduler status after a delay
      setTimeout(() => loadSchedulerStatus(), 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar backup');
    } finally {
      setUploadingToOneDrive(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Database className="h-8 w-8 text-primary" />
          Backup y Restauración
        </h1>
        <p className="text-muted-foreground mt-1">
          Gestiona copias de seguridad de tu base de datos
        </p>
      </div>

      {/* Database Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            Estado de la Base de Datos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : backupInfo ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <HardDrive className="h-6 w-6 mx-auto mb-2 text-blue-500" />
                  <p className="text-2xl font-bold">{backupInfo.total_documents}</p>
                  <p className="text-xs text-muted-foreground">Documentos totales</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <FileJson className="h-6 w-6 mx-auto mb-2 text-green-500" />
                  <p className="text-2xl font-bold">~{backupInfo.estimated_size_kb} KB</p>
                  <p className="text-xs text-muted-foreground">Tamaño estimado</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 text-center col-span-2">
                  <Calendar className="h-6 w-6 mx-auto mb-2 text-amber-500" />
                  <p className="text-lg font-medium">{backupInfo.recommendation}</p>
                  <p className="text-xs text-muted-foreground">Frecuencia recomendada</p>
                </div>
              </div>
              
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3">Documentos por colección:</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-sm">
                  {Object.entries(backupInfo.collections).map(([name, count]) => (
                    <div key={name} className="flex justify-between bg-muted/30 rounded px-3 py-1">
                      <span className="text-muted-foreground">{name}:</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">No se pudo cargar la información</p>
          )}
        </CardContent>
      </Card>

      {/* Atlas Dual-Write Backup */}
      <Card data-testid="atlas-backup-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5" />
            Respaldo en MongoDB Atlas
          </CardTitle>
          <CardDescription>
            Réplica en tiempo real: cada cambio en la base principal se copia a tu MongoDB Atlas
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingAtlas ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between rounded-lg border p-4" data-testid="atlas-status-row">
                <div className="flex items-center gap-3">
                  {atlasStatus?.enabled && atlasStatus?.reachable ? (
                    <CheckCircle className="h-6 w-6 text-green-500" />
                  ) : (
                    <AlertTriangle className="h-6 w-6 text-amber-500" />
                  )}
                  <div>
                    <p className="font-medium">
                      {atlasStatus?.enabled && atlasStatus?.reachable
                        ? 'Conectado a Atlas'
                        : atlasStatus?.enabled
                          ? 'Configurado, pero no se pudo conectar'
                          : 'No configurado'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {atlasStatus?.database
                        ? `Base de datos: ${atlasStatus.database}`
                        : atlasStatus?.message || 'Agrega MONGO_BACKUP_URL para activar la réplica'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadAtlasStatus}
                  data-testid="atlas-refresh-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Actualizar
                </Button>
              </div>

              {atlasStatus?.enabled && atlasStatus?.reachable && (
                <>
                  {atlasStatus.collections && (
                    <div className="border rounded-lg p-4">
                      <h4 className="font-medium mb-3">Documentos por colección en Atlas:</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-sm">
                        {Object.entries(atlasStatus.collections).map(([name, count]) => (
                          <div key={name} className="flex justify-between bg-muted/30 rounded px-3 py-1">
                            <span className="text-muted-foreground">{name}:</span>
                            <span className="font-medium">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => setShowAtlasSyncDialog(true)}
                      disabled={syncingAtlas}
                      data-testid="atlas-sync-btn"
                    >
                      {syncingAtlas ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      Ejecutar copia inicial
                    </Button>
                    <Button
                      variant="outline"
                      onClick={runAtlasVerify}
                      disabled={verifyingAtlas}
                      data-testid="atlas-verify-btn"
                    >
                      {verifyingAtlas ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ShieldCheck className="h-4 w-4 mr-2" />
                      )}
                      Verificar restauración
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowAtlasRestoreDialog(true)}
                      disabled={restoringAtlas}
                      data-testid="atlas-restore-btn"
                    >
                      {restoringAtlas ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      Restaurar desde Atlas
                    </Button>
                  </div>

                  {atlasVerify && (
                    <div className="border rounded-lg p-4" data-testid="atlas-verify-result">
                      <div className="flex items-center gap-2 mb-3">
                        {atlasVerify.all_match ? (
                          <>
                            <CheckCircle className="h-5 w-5 text-green-500" />
                            <span className="font-medium">Todos los conteos coinciden</span>
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                            <span className="font-medium">Hay diferencias de conteo</span>
                            <Button
                              size="sm"
                              className="ml-auto"
                              onClick={repairAtlasDiffs}
                              disabled={repairingAtlas}
                              data-testid="atlas-repair-btn"
                            >
                              {repairingAtlas ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <RefreshCw className="h-4 w-4 mr-2" />
                              )}
                              Reparar diferencias
                            </Button>
                          </>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        {atlasVerify.collections.map((c) => (
                          <div
                            key={c.collection}
                            className={`flex justify-between rounded px-3 py-1 ${c.match ? 'bg-muted/30' : 'bg-amber-500/10'}`}
                          >
                            <span className="text-muted-foreground">{c.collection}</span>
                            <span className="font-medium">
                              {c.primary} → {c.backup} {c.match ? '✓' : '⚠'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* OneDrive Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5" />
            Backup a OneDrive
          </CardTitle>
          <CardDescription>
            Sube tus backups automáticamente a tu cuenta de Microsoft OneDrive
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingOneDrive ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : !oneDriveStatus.connected ? (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-6 text-center">
              <Cloud className="h-12 w-12 mx-auto mb-4 text-blue-500" />
              <h3 className="font-medium text-lg mb-2">Conecta tu cuenta de OneDrive</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Guarda tus backups de forma segura en tu OneDrive personal o empresarial.
                Los archivos se guardarán en la carpeta "CMIdiomas_Backups".
              </p>
              <Button onClick={handleConnectOneDrive}>
                <Cloud className="mr-2 h-4 w-4" />
                Conectar OneDrive
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Connected Status */}
              <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                  <div>
                    <p className="font-medium">OneDrive conectado</p>
                    <p className="text-sm text-muted-foreground">
                      {oneDriveStatus.user_name} ({oneDriveStatus.user_email})
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowDisconnectDialog(true)}
                >
                  <Unplug className="mr-2 h-4 w-4" />
                  Desconectar
                </Button>
              </div>

              {/* Last Backup */}
              {oneDriveStatus.last_backup && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 rounded-lg px-4 py-2">
                  <Clock className="h-4 w-4" />
                  Último backup: {new Date(oneDriveStatus.last_backup).toLocaleString()}
                </div>
              )}

              {/* Upload Button */}
              <Button 
                onClick={handleUploadToOneDrive}
                disabled={uploadingToOneDrive}
                className="w-full"
                size="lg"
              >
                {uploadingToOneDrive ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Subiendo backup a OneDrive...
                  </>
                ) : (
                  <>
                    <Cloud className="mr-2 h-4 w-4" />
                    Subir Backup Ahora a OneDrive
                  </>
                )}
              </Button>

              {/* Auto Backup Config */}
              <div className="border rounded-lg p-4 space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Configuración de Backup Automático
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Frecuencia</Label>
                    <Select 
                      value={oneDriveStatus.frequency} 
                      onValueChange={(v) => setOneDriveStatus({...oneDriveStatus, frequency: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="every_6_hours">Cada 6 horas</SelectItem>
                        <SelectItem value="every_12_hours">Cada 12 horas</SelectItem>
                        <SelectItem value="daily">Diario</SelectItem>
                        <SelectItem value="every_2_days">Cada 2 días</SelectItem>
                        <SelectItem value="every_3_days">Cada 3 días</SelectItem>
                        <SelectItem value="weekly">Semanal</SelectItem>
                        <SelectItem value="every_2_weeks">Cada 2 semanas</SelectItem>
                        <SelectItem value="monthly">Mensual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Hora preferida</Label>
                    <Select 
                      value={oneDriveStatus.preferred_hour || '03'}
                      onValueChange={(v) => setOneDriveStatus({...oneDriveStatus, preferred_hour: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="00">12:00 AM (medianoche)</SelectItem>
                        <SelectItem value="03">3:00 AM</SelectItem>
                        <SelectItem value="06">6:00 AM</SelectItem>
                        <SelectItem value="09">9:00 AM</SelectItem>
                        <SelectItem value="12">12:00 PM (mediodía)</SelectItem>
                        <SelectItem value="15">3:00 PM</SelectItem>
                        <SelectItem value="18">6:00 PM</SelectItem>
                        <SelectItem value="21">9:00 PM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Estado</Label>
                    <div className="flex items-center gap-2 h-10">
                      <Checkbox
                        id="autoBackupEnabled"
                        checked={oneDriveStatus.auto_enabled}
                        onCheckedChange={(c) => setOneDriveStatus({...oneDriveStatus, auto_enabled: c})}
                      />
                      <label htmlFor="autoBackupEnabled" className="cursor-pointer text-sm">
                        Activar backup automático
                      </label>
                    </div>
                  </div>
                </div>

                {/* Retention Policy */}
                <div className="space-y-2">
                  <Label>Retención de backups</Label>
                  <Select 
                    value={oneDriveStatus.retention || '30'}
                    onValueChange={(v) => setOneDriveStatus({...oneDriveStatus, retention: v})}
                  >
                    <SelectTrigger className="w-full md:w-1/2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Mantener últimos 7 días</SelectItem>
                      <SelectItem value="14">Mantener últimos 14 días</SelectItem>
                      <SelectItem value="30">Mantener últimos 30 días</SelectItem>
                      <SelectItem value="60">Mantener últimos 60 días</SelectItem>
                      <SelectItem value="90">Mantener últimos 90 días</SelectItem>
                      <SelectItem value="unlimited">Sin límite (mantener todos)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Los backups más antiguos que este período se eliminarán automáticamente de OneDrive.
                  </p>
                </div>

                {/* Scheduler Status */}
                {schedulerStatus.scheduler_running && (
                  <div className={`rounded-lg p-4 ${schedulerStatus.job_scheduled ? 'bg-green-500/10 border border-green-500/20' : 'bg-amber-500/10 border border-amber-500/20'}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <Clock className={`h-5 w-5 ${schedulerStatus.job_scheduled ? 'text-green-500' : 'text-amber-500'}`} />
                      <span className="font-medium">
                        {schedulerStatus.job_scheduled ? 'Programador Activo' : 'Programador Inactivo'}
                      </span>
                    </div>
                    {schedulerStatus.next_run && (
                      <p className="text-sm text-muted-foreground ml-8">
                        Próximo backup: <strong>{new Date(schedulerStatus.next_run).toLocaleString()}</strong>
                      </p>
                    )}
                    {schedulerStatus.recent_backups?.length > 0 && (
                      <div className="mt-3 ml-8">
                        <p className="text-xs text-muted-foreground mb-1">Últimos backups:</p>
                        <div className="space-y-1">
                          {schedulerStatus.recent_backups.slice(0, 3).map((backup, idx) => (
                            <div key={idx} className="text-xs flex items-center gap-2 text-muted-foreground">
                              <span className={backup.status === 'success' ? 'text-green-500' : 'text-red-500'}>●</span>
                              <span>{new Date(backup.timestamp).toLocaleString()}</span>
                              <span>- {backup.filename}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button onClick={handleSaveAutoConfig} variant="outline" className="flex-1">
                    Guardar Configuración
                  </Button>
                  {oneDriveStatus.connected && oneDriveStatus.auto_enabled && (
                    <Button onClick={handleRunBackupNow} disabled={uploadingToOneDrive} variant="default">
                      {uploadingToOneDrive ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Ejecutar Ahora'
                      )}
                    </Button>
                  )}
                </div>

                <p className="text-xs text-muted-foreground">
                  * Los backups automáticos se ejecutarán según la frecuencia seleccionada.
                </p>

                {/* Restore from OneDrive Section */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        <Download className="h-4 w-4 text-primary" />
                        Restaurar desde OneDrive
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Selecciona un backup para restaurar la base de datos
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={loadOneDriveBackupList}
                      disabled={loadingOneDriveBackups}
                    >
                      {loadingOneDriveBackups ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Actualizar Lista'
                      )}
                    </Button>
                  </div>
                  
                  {loadingOneDriveBackups ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : oneDriveBackups.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Cloud className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>No hay backups disponibles en OneDrive</p>
                      <p className="text-sm">Sube un backup primero usando el botón de arriba</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {oneDriveBackups.map((backup) => (
                        <div 
                          key={backup.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedOneDriveBackup?.id === backup.id 
                              ? 'border-primary bg-primary/5' 
                              : 'hover:border-muted-foreground/50'
                          }`}
                          onClick={() => setSelectedOneDriveBackup(backup)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileJson className="h-5 w-5 text-blue-500" />
                              <div>
                                <p className="font-mono text-sm">{backup.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(backup.modified).toLocaleString()} • {(backup.size / 1024).toFixed(1)} KB
                                </p>
                              </div>
                            </div>
                            {selectedOneDriveBackup?.id === backup.id && (
                              <CheckCircle className="h-5 w-5 text-primary" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {selectedOneDriveBackup && (
                    <Button 
                      className="w-full mt-4 bg-amber-600 hover:bg-amber-700"
                      onClick={() => setShowOneDriveRestoreDialog(true)}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Restaurar: {selectedOneDriveBackup.name}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Backup */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Descargar Backup Manual
          </CardTitle>
          <CardDescription>
            Descarga una copia de tus datos en formato JSON a tu computadora
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Backup Type Selection */}
          <div className="space-y-3">
            <Label>Tipo de Backup</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="backupType"
                  value="all"
                  checked={backupType === 'all'}
                  onChange={() => setBackupType('all')}
                  className="w-4 h-4"
                />
                <span>Backup completo (todo)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="backupType"
                  value="selective"
                  checked={backupType === 'selective'}
                  onChange={() => setBackupType('selective')}
                  className="w-4 h-4"
                />
                <span>Backup selectivo</span>
              </label>
            </div>
          </div>

          {/* Collection Selection */}
          {backupType === 'selective' && (
            <div className="space-y-3 border rounded-lg p-4">
              <div className="flex justify-between items-center">
                <Label>Seleccionar colecciones:</Label>
                <div className="space-x-2">
                  <Button variant="ghost" size="sm" onClick={selectAllCollections}>
                    Seleccionar todo
                  </Button>
                  <Button variant="ghost" size="sm" onClick={deselectAllCollections}>
                    Deseleccionar todo
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {COLLECTIONS.map(collection => (
                  <label
                    key={collection.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedCollections.includes(collection.id)
                        ? 'bg-primary/10 border-primary'
                        : 'bg-muted/30 hover:bg-muted/50'
                    }`}
                  >
                    <Checkbox
                      checked={selectedCollections.includes(collection.id)}
                      onCheckedChange={() => toggleCollection(collection.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span>{collection.icon}</span>
                        <span className="font-medium text-sm">{collection.name}</span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {collection.description}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
              {selectedCollections.length === 0 && (
                <p className="text-sm text-amber-500">⚠️ Selecciona al menos una colección</p>
              )}
            </div>
          )}

          <Button 
            onClick={handleDownloadBackup} 
            disabled={downloading || (backupType === 'selective' && selectedCollections.length === 0)}
            className="w-full"
            size="lg"
          >
            {downloading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generando backup...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Descargar {backupType === 'all' ? 'Backup Completo' : `${selectedCollections.length} Colecciones`}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Restore */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Restaurar Backup
          </CardTitle>
          <CardDescription>
            Restaura tus datos desde un archivo de backup anterior
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
              <div>
                <p className="font-medium text-amber-500">¡Precaución!</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Restaurar un backup <strong>reemplazará todos los datos actuales</strong>. 
                  Asegúrate de descargar un backup actual antes de continuar.
                </p>
              </div>
            </div>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <Button 
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={restoring}
            className="w-full"
            size="lg"
          >
            <Upload className="mr-2 h-4 w-4" />
            Seleccionar Archivo de Backup
          </Button>
        </CardContent>
      </Card>

      {/* Disconnect OneDrive Dialog */}
      <Dialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Unplug className="h-5 w-5 text-amber-500" />
              Desconectar OneDrive
            </DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que quieres desconectar tu cuenta de OneDrive?
              Los backups automáticos se desactivarán.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisconnectDialog(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDisconnectOneDrive}>
              Sí, Desconectar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirmar Restauración
            </DialogTitle>
            <DialogDescription>
              Estás a punto de restaurar la base de datos desde:
              <br />
              <strong className="text-foreground">{selectedFile?.name}</strong>
              <br /><br />
              Esta acción <strong>eliminará todos los datos actuales</strong> y los reemplazará 
              con los datos del backup. Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRestoreDialog(false);
                setSelectedFile(null);
              }}
              disabled={restoring}
            >
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleRestore} disabled={restoring}>
              {restoring ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Restaurando...
                </>
              ) : (
                'Sí, Restaurar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore from OneDrive Confirmation Dialog */}
      <Dialog open={showOneDriveRestoreDialog} onOpenChange={setShowOneDriveRestoreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cloud className="h-5 w-5 text-blue-500" />
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Restaurar desde OneDrive
            </DialogTitle>
            <DialogDescription>
              <div className="space-y-3 mt-2">
                <p>
                  Estás a punto de restaurar la base de datos desde OneDrive:
                </p>
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <div className="flex items-center gap-2">
                    <FileJson className="h-5 w-5 text-blue-500" />
                    <span className="font-mono font-medium">{selectedOneDriveBackup?.name}</span>
                  </div>
                  {selectedOneDriveBackup?.modified && (
                    <p className="text-xs text-muted-foreground mt-1 ml-7">
                      Fecha: {new Date(selectedOneDriveBackup.modified).toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400">
                  <p className="font-medium">⚠️ Advertencia</p>
                  <p className="text-sm">
                    Esta acción <strong>eliminará TODOS los datos actuales</strong> y los reemplazará 
                    con los datos del backup. Esta acción NO se puede deshacer.
                  </p>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowOneDriveRestoreDialog(false);
              }}
              disabled={restoringFromOneDrive}
            >
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRestoreFromOneDrive} 
              disabled={restoringFromOneDrive}
            >
              {restoringFromOneDrive ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Restaurando desde OneDrive...
                </>
              ) : (
                <>
                  <Cloud className="mr-2 h-4 w-4" />
                  Sí, Restaurar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Atlas initial sync confirmation */}
      <Dialog open={showAtlasSyncDialog} onOpenChange={setShowAtlasSyncDialog}>
        <DialogContent data-testid="atlas-sync-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Copia inicial a Atlas
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p>
                  Se copiarán TODOS los documentos de la base principal a tu MongoDB Atlas
                  mediante <strong>upsert por ID</strong>.
                </p>
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <p className="text-sm">
                    Es una operación <strong>segura y no destructiva</strong>: no elimina datos
                    existentes en Atlas, solo actualiza o agrega. Puede tardar según el volumen.
                  </p>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAtlasSyncDialog(false)} disabled={syncingAtlas}>
              Cancelar
            </Button>
            <Button onClick={runAtlasSync} disabled={syncingAtlas} data-testid="atlas-sync-confirm-btn">
              {syncingAtlas ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Copiando...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Sí, ejecutar copia
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Atlas restore confirmation */}
      <Dialog open={showAtlasRestoreDialog} onOpenChange={setShowAtlasRestoreDialog}>
        <DialogContent data-testid="atlas-restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Restaurar desde Atlas
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p>
                  Se traerán TODOS los documentos desde tu MongoDB Atlas hacia la base principal
                  mediante <strong>upsert por ID</strong> (actualiza o agrega).
                </p>
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400">
                  <p className="font-medium">⚠️ Importante</p>
                  <p className="text-sm">
                    Los documentos de la base principal con el mismo ID serán
                    <strong> sobrescritos</strong> con los de Atlas. No se eliminan los documentos
                    que existan solo en la base principal.
                  </p>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAtlasRestoreDialog(false)} disabled={restoringAtlas}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={runAtlasRestore} disabled={restoringAtlas} data-testid="atlas-restore-confirm-btn">
              {restoringAtlas ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Restaurando...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Sí, restaurar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BackupPage;
